/**
 *
 * Formatar telefone brasileiro
 *
 */
export function formatPhone(phone: string) {
  if (!phone) {
    return '';
  }
  // Remove tudo o que não é dígito
  phone = phone.replace(/\D/g, '');
  // Coloca hífen entre o quarto e o quinto dígitos
  phone = phone.replace(/(\d)(\d{4})$/, '$1-$2');
  // Remover zero na primeira posição se
  phone = phone.charAt(0) === '0' ? phone.substring(1, phone.length) : phone;
  // Coloca parênteses em volta dos dois primeiros dígitos
  phone = phone.length > 10 ? phone.replace(/^(\d{2})(\d)/g, '($1) $2') : phone;
  return phone;
}

/**
 *
 * Formatar documento de pessoa com base no tipo informado
 *
 */
export function formatPersonDocument(document: string, type?: string): string {
  const lowerType = type?.toLowerCase() ?? '';
  switch (lowerType) {
    case 'cpf':
      return formatCPF(document);
    case 'cnpj':
      return formatCNPJ(document);
    case 'cuit':
      return formatCUIT(document);
    default:
      return document;
  }
}

function formatCPF(cpf: string): string {
  // Remover tudo que não seja dígito
  const result = cpf.replace(/\D/g, '');
  // Retornar CPF formatado
  return `${result.slice(0, 3)}.${result.slice(3, 6)}.${result.slice(6, 9)}-${result.slice(9, 11)}`;
}

function formatCNPJ(cnpj: string): string {
  // Mantém apenas letras e números
  const result = cnpj.toUpperCase().replace(/[^A-Z0-9]/g, '');

  if (result.length < 14) {
    return result;
  }

  return `${result.slice(0, 2)}.${result.slice(2, 5)}.${result.slice(5, 8)}/${result.slice(8, 12)}-${result.slice(12, 14)}`;
}

/** Formatar documento da Argentina */
function formatCUIT(cuit: string) {
  // Remove caracteres não numéricos
  cuit = cuit.replace(/\D/g, '');

  if (cuit.length !== 11) {
    return cuit;
  }

  return `${cuit.slice(0, 2)}-${cuit.slice(2, 10)}-${cuit.slice(10)}`;
}

/**
 * Preenche com zeros a esquerda.
 * @param value Valor que será completado
 * @param length Tamanho esperado da String para completar com zeros a esquerda
 */
export function pad(value: string | number, length: number): string {
  const resto = length - String(value).length;
  return '0'.repeat(resto > 0 ? resto : 0) + value;
}

function isValidCPF(cpf: string): boolean {
  // Remove todos os caracteres não numéricos
  cpf = cpf.replace(/[^\d]+/g, '');
  // Verifica se o CPF tem 11 dígitos
  if (cpf.length !== 11) {
    return false;
  }
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cpf)) {
    return false;
  }

  // Valida os dígitos verificadores
  let sum = 0;
  let remainder;

  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
  }

  remainder = (sum * 10) % 11;

  if (remainder === 10 || remainder === 11) {
    remainder = 0;
  }
  if (remainder !== parseInt(cpf.substring(9, 10))) {
    return false;
  }

  sum = 0;

  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
  }

  remainder = (sum * 10) % 11;

  if (remainder === 10 || remainder === 11) {
    remainder = 0;
  }
  if (remainder !== parseInt(cpf.substring(10, 11))) {
    return false;
  }

  return true;
}

function isValidCNPJ(cnpj: string): boolean {
  // Remove caracteres de formatação
  cnpj = cnpj.toUpperCase().replace(/[^A-Z0-9]/g, '');

  // Deve possuir 14 caracteres
  if (cnpj.length !== 14) {
    return false;
  }

  // As 12 primeiras posições podem ser letras ou números.
  // Os dois últimos caracteres (DV) devem ser números.
  if (!/^[A-Z0-9]{12}\d{2}$/.test(cnpj)) {
    return false;
  }

  // Evita sequências iguais (ex.: 111..., AAAAA..., BBBBB...)
  if (/^(.)\1+$/.test(cnpj)) {
    return false;
  }

  const charValue = (char: string): number => {
    const code = char.charCodeAt(0);

    // 0-9
    if (code >= 48 && code <= 57) {
      return code - 48;
    }

    // A-Z
    if (code >= 65 && code <= 90) {
      return code - 48;
    }

    return -1;
  };

  const calculateDigit = (base: string): number => {
    let sum = 0;
    let pos = base.length - 7;

    for (let i = 0; i < base.length; i++) {
      sum += charValue(base[i]) * pos--;

      if (pos < 2) {
        pos = 9;
      }
    }

    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const numbers = cnpj.substring(0, 12);
  const digit1 = calculateDigit(numbers);

  if (digit1 !== Number(cnpj[12])) {
    return false;
  }

  const digit2 = calculateDigit(numbers + digit1);

  if (digit2 !== Number(cnpj[13])) {
    return false;
  }

  return true;
}

/** Validar de documento de pessoa física ou jurídica da Argentina é válido */
function isValidCUIT(cuit: string) {
  // Remove hífens, espaços e pontos
  cuit = cuit.replace(/[-.\s]/g, '');

  // Verifica se contém exatamente 11 dígitos
  if (!/^\d{11}$/.test(cuit)) {
    return false;
  }

  const coeficientes = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let soma = 0;

  for (let i = 0; i < 10; i++) {
    soma += parseInt(cuit[i], 10) * coeficientes[i];
  }

  const resto = soma % 11;
  let digitoVerificador = 11 - resto;

  if (digitoVerificador === 11) {
    digitoVerificador = 0;
  } else if (digitoVerificador === 10) {
    digitoVerificador = 9;
  }

  return digitoVerificador === parseInt(cuit[10], 10);
}

/**
 * Validar do documento da pessoa com base no tipo informado
 */
export function isValidPersonDocument(document: string, type: string): boolean {
  const lowerType = type?.toLowerCase() ?? '';
  switch (lowerType) {
    case 'cpf':
      return isValidCPF(document);
    case 'cnpj':
      return isValidCNPJ(document);
    case 'cuit':
      return isValidCUIT(document);
    default:
      return true;
  }
}

/**
 * Método de validação do email
 * @param email Email que deverá ser válidado
 * @return Retorna TRUE se o email for válido
 */
export function isValidEmail(email: string) {
  const filtro = /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/i;
  return !!filtro.test(email);
}

/**
 * Retornar string com a primeira letra de cada palavra maiúscula
 */
export function titleCase(str: string): string {
  return str.toLowerCase().replace(/(?:^|\s)\w/g, function (match) {
    return match.toUpperCase();
  });
}

/**
 * Retornar a string com apenas a primeira letra da primeira palavra maiúscula
 */
export function capitalize(str: string): string {
  return str.replace(/^(.)(.*)$/, (match, firstLetter, restOfString) => {
    return firstLetter.toUpperCase() + restOfString;
  });
}
